import { useEffect, useState, useMemo, useCallback, useRef } from 'react'
import { useIntl } from 'react-intl'
import { useDispatch, useSelector } from 'react-redux'
import { clearGpsVersionStatus, selectCourse } from 'store/course/slice'
import CourseGpsEditorTutorial from 'components/gps-editor/CourseGpsEditorTutorial'
import CustomTooltip from 'components/ui/CustomTooltip'
import useUserRole from 'hooks/useUserRole'
import CourseGpsEditorSidebar from 'components/gps-editor/CourseGpsEditorSidebar'
import CourseGpsEditorHeader from 'components/gps-editor/CourseGpsEditorHeader'
import CourseGpsEditorGreenAutoMapDialog from 'components/gps-editor/CourseGpsEditorGreenAutoMapDialog'
import CourseGpsEditorGreenAutoMapControls from 'components/gps-editor/CourseGpsEditorGreenAutoMapControls'
import CourseGpsHoleNavigator from 'components/gps-editor/CourseGpsHoleNavigator'
import CourseGpsEditorSavePopover from 'components/gps-editor/CourseGpsEditorSavePopover'
import CourseGpsEditorToolbar from 'components/gps-editor/CourseGpsEditorToolbar'
import useGpsEditorToolModes from 'components/gps-editor/hooks/useGpsEditorToolModes'
import {
  GreenAutoMapNumericParamKey,
  GreenAutoMapParamKey,
  GreenAutoMapSliderConfig,
} from 'components/gps-editor/greenAutoMapControlConfig'
import { useLocation } from 'react-router-dom'
import { GPS_EDITOR_TUTORIAL } from 'store/course/utils'
import OverlayLoader from 'components/ui/OverlayLoader'
import { getCourse } from 'store/course/thunks'
import { IconButton } from '@mui/material'
import { KeyboardArrowDown } from '@mui/icons-material'
import { DrawerToggleLeftIcon } from 'assets/icons/drawer-toggle-left'
import GreenAutomapperScreenshot from 'assets/images/green-automapper-screenshot.jpg'
import { DEFAULT_GREEN_AUTO_MAP_PARAMS, GreenAutoMapParams } from 'utils/gpsEditor/gpsUtils'
import {
  ActionButtons,
  HorizontalIconDivider,
  MapControlIcons,
  RotateControls,
  SaveButton,
  SaveButtonText,
  TiltButton,
  ToolbarPlaceholder,
  ToolbarStack,
  ToolbarWrapper,
  ZoomControls,
} from 'components/gps-editor/CourseGpsEditorElements.styles'
import CourseGpsEditorGreenAutoMapSuccessDialog from './CourseGpsEditorGreenAutoMapSuccessDialog'
import { MapTilesIcon } from 'assets/icons/map-tiles'
import { ClockwiseIcon } from 'assets/icons/clockwise'
import { CounterClockwiseIcon } from 'assets/icons/counterclockwise'
import ConfirmGpsSaveDialog from './ConfirmGpsSaveDialog'
import CourseGpsSaveSuccessDialog from './CourseGpsSaveSuccessDialog'

interface Props {
  activeHole: number
  markers: CourseGpsMarker[]
  polygons: GpsEditorPolygon[]
  versionIsDirty: boolean
  playableHoleCount: number
  maxGpsPoints: number
  autoLoad: (e: any) => void
  placeChanged: () => void
  syncSelectedVersion: (version: number) => void
  selectedGpsVersion: number | null
  resetMarkers: (markers?: CourseGpsMarker[], polygons?: GpsEditorPolygon[]) => void
  resetZoom: () => void
  syncHoleTool: (currentState: boolean) => void
  syncGreenMapping: (currentState: boolean) => void
  greenAutoMapControlsVisible: boolean
  greenAutoMapBusy: boolean
  greenAutoMapParams: GreenAutoMapParams
  onGreenAutoMapParamChange: (key: GreenAutoMapParamKey, value: GreenAutoMapParams[GreenAutoMapParamKey]) => void
  onResetGreenAutoMapParams: () => void
  greenAutoMapPreviewReady: boolean
  onCloseGreenAutoMapControls: () => void
  onAcceptGreenAutoMapPreview: () => void
  onConfirmManualGuidedGreen: () => void
  guidedGreenAutoMapActive: boolean
  guidedGreenAutoMapCompletedOpen: boolean
  guidedGreenAutoMapVerifiedCount: number
  guidedGreenAutoMapTotalCount: number
  onCloseGuidedGreenAutoMapCompleted: () => void
  onStartGuidedGreenAutoMap: () => void
  greenPolygonFinished: boolean
  onAutoMapGreen: (params?: GreenAutoMapParams) => void
  onFinishGreenPolygon: () => void
  onClearGreenPolygon: () => void
  goToPreviousHole: () => void
  goToNextHole: () => void
  goToHole: (hole: number) => void
  triggerZoom: (direction: '+' | '-') => void
  triggerMapTilt: () => void
  triggerMapRotate: (direction: 'clockwise' | 'counterclockwise') => void
  mapTiltEnabled: boolean
  mapTiltAvailable: boolean
  handleSubmit: (makeOfficial?: boolean, populateRemainingHoles?: boolean) => void
  canUndo: boolean
  canRedo: boolean
  onUndo: () => void
  onRedo: () => void
  onRecordBeforeChange: () => void
  registerActivateGreenTool?: (fn: (() => void) | null) => void
}

export default function CourseGpsEditorElements({
  activeHole,
  markers,
  polygons,
  versionIsDirty,
  playableHoleCount,
  maxGpsPoints,
  autoLoad,
  placeChanged,
  syncSelectedVersion,
  selectedGpsVersion,
  resetMarkers,
  resetZoom,
  syncHoleTool,
  syncGreenMapping,
  greenAutoMapControlsVisible,
  greenAutoMapBusy,
  greenAutoMapParams,
  onGreenAutoMapParamChange,
  onResetGreenAutoMapParams,
  greenAutoMapPreviewReady,
  onCloseGreenAutoMapControls,
  onAcceptGreenAutoMapPreview,
  onConfirmManualGuidedGreen,
  guidedGreenAutoMapActive,
  guidedGreenAutoMapCompletedOpen,
  guidedGreenAutoMapVerifiedCount,
  guidedGreenAutoMapTotalCount,
  onCloseGuidedGreenAutoMapCompleted,
  onStartGuidedGreenAutoMap,
  greenPolygonFinished,
  onAutoMapGreen,
  onFinishGreenPolygon,
  onClearGreenPolygon,
  goToPreviousHole,
  goToNextHole,
  goToHole,
  triggerZoom,
  triggerMapTilt,
  triggerMapRotate,
  mapTiltEnabled,
  mapTiltAvailable,
  handleSubmit,
  canUndo,
  canRedo,
  onUndo,
  onRedo,
  onRecordBeforeChange,
  registerActivateGreenTool,
}: Props): JSX.Element | null {
  const greenAutoMapResetExcludedKeys = useMemo<GreenAutoMapParamKey[]>(
    () => ['debugStopLogging', 'debugImageLogging'],
    [],
  )

  const intl = useIntl()
  const dispatch = useDispatch()
  const location = useLocation()
  const lastRequestedCourseIdRef = useRef<number | undefined>(undefined)
  const [locationSelected, setLocationSelected] = useState<boolean>(false)
  const [saveDialogOpen, setSaveDialogOpen] = useState<boolean>(false)
  const [savePopoverOpen, setSavePopoverOpen] = useState<boolean>(false)
  const [savePopoverAnchorEl, setSavePopoverAnchorEl] = useState<HTMLElement | null>(null)
  const [makeOfficial, setMakeOfficial] = useState<boolean>(false)
  const [populateRemainingHoles, setPopulateRemainingHoles] = useState<boolean>(false)
  const [sidebarOpen, setSidebarOpen] = useState<boolean>(false)
  const [greenAutoMapAdvancedOpen, setGreenAutoMapAdvancedOpen] = useState<boolean>(false)
  const [guidedGreenAutoMapPromptOpen, setGuidedGreenAutoMapPromptOpen] = useState<boolean>(false)
  const [greenAutoMapDraftParams, setGreenAutoMapDraftParams] = useState<GreenAutoMapParams>({
    ...greenAutoMapParams,
  })
  const activeGreenAutoMapSliderKeyRef = useRef<GreenAutoMapNumericParamKey | null>(null)
  const greenAutoMapDraftParamsRef = useRef<GreenAutoMapParams>({
    ...greenAutoMapParams,
  })
  const greenAutoMapParamsRef = useRef<GreenAutoMapParams>({
    ...greenAutoMapParams,
  })
  const lastPromptedGpsVersionRef = useRef<number | null>(null)
  const { isAdmin, isAdminCourseEditor, isGpsEditor } = useUserRole()
  const {
    course,
    gpsEditorTutorialPhase,
    status,
    gpsVersionStatus,
    gpsVersionOfficialStatus,
    gpsVersionZendeskStatus,
  } = useSelector(selectCourse)
  const actionButtonsRef = useRef<HTMLDivElement | null>(null)
  const { greenMapping, holeToolEnabled, selectEditTool, toggleGreenMapping, toggleHoleTool } = useGpsEditorToolModes({
    syncGreenMapping,
    syncHoleTool,
  })

  useEffect(() => {
    if (!registerActivateGreenTool) {
      return
    }
    registerActivateGreenTool(() => {
      if (!greenMapping) {
        toggleGreenMapping()
      }
    })
    return () => registerActivateGreenTool(null)
  }, [greenMapping, registerActivateGreenTool, toggleGreenMapping])

  const hasAdminAccess = isAdmin || isAdminCourseEditor
  const showSidebar = hasAdminAccess
  const hasExistingGpsPoints = Boolean(course.totalGpsPoints)
  const nonFairwayMarkersCount = markers.filter((marker: CourseGpsMarker) => marker.locationType !== 'fairway').length
  const saveSucceeded = gpsVersionZendeskStatus === 'succeeded'
  const saveStatus: RequestStatus = (() => {
    if (gpsVersionStatus === 'loading' || gpsVersionOfficialStatus === 'loading') {
      return 'loading'
    }
    if (gpsVersionStatus === 'failed' || gpsVersionOfficialStatus === 'failed') {
      return 'failed'
    }
    if (gpsVersionStatus === 'succeeded' && (!makeOfficial || gpsVersionOfficialStatus === 'succeeded')) {
      return 'succeeded'
    }
    return 'idle'
  })()
  const hasAllPlayableHoles = playableHoleCount === 18
  const canPopulateIncompleteCourse = playableHoleCount > 0 && playableHoleCount < 18
  const shouldOfferGuidedGreenAutoMap = useMemo(() => {
    return hasAllPlayableHoles && !polygons.some((polygon) => Boolean(polygon.isFinished))
  }, [hasAllPlayableHoles, polygons])
  const guidedGreenAutoMapProgressLabel = useMemo(() => {
    if (!guidedGreenAutoMapActive || guidedGreenAutoMapTotalCount === 0) {
      return null
    }

    return `${guidedGreenAutoMapVerifiedCount}/${guidedGreenAutoMapTotalCount} verified`
  }, [guidedGreenAutoMapActive, guidedGreenAutoMapTotalCount, guidedGreenAutoMapVerifiedCount])

  const courseId = useMemo(() => {
    const params = new URLSearchParams(location.search)
    const value = params.get('courseId')
    return value ? parseInt(value) : undefined
  }, [location.search])

  const currentOfficialGpsVersion: number = useMemo(() => {
    const gpsPoints = course?.gpsPoints || []
    const versions = [...new Map(gpsPoints.map((gps: CourseGps) => [gps.gpsVersion, gps])).values()]
    return versions.find((gps) => gps.isOfficial)?.gpsVersion || 0
  }, [course])

  useEffect(() => {
    if (courseId && lastRequestedCourseIdRef.current !== courseId) {
      lastRequestedCourseIdRef.current = courseId
      dispatch(getCourse(courseId))
    }
  }, [courseId, dispatch])

  useEffect(() => {
    if (hasAdminAccess) {
      return
    }
    syncSelectedVersion(currentOfficialGpsVersion)
  }, [syncSelectedVersion, currentOfficialGpsVersion, hasAdminAccess])

  const saveActionUnavailable = useMemo(() => {
    const noChangesToExistingVersion = hasExistingGpsPoints && !versionIsDirty
    return playableHoleCount === 0 || noChangesToExistingVersion
  }, [hasExistingGpsPoints, versionIsDirty, playableHoleCount])

  const isCourseLocationOnly = playableHoleCount === 1
  const makeOfficialAllowedHoleCount = hasAllPlayableHoles || isCourseLocationOnly
  const makeOfficialDisabled = !makeOfficialAllowedHoleCount

  const makeOfficialDisabledReason = useMemo(() => {
    if (!makeOfficialAllowedHoleCount) {
      return 'Blocked until 18 holes are set'
    }
    return null
  }, [makeOfficialAllowedHoleCount])

  const finishEditingDisabled = useMemo(() => {
    if (hasAdminAccess) {
      return saveActionUnavailable
    }

    return saveActionUnavailable || !hasAllPlayableHoles
  }, [hasAllPlayableHoles, hasAdminAccess, saveActionUnavailable])

  const saveAndFinishDisabled = saveActionUnavailable

  const isDraftSave = playableHoleCount > 1 && playableHoleCount < 18 && !populateRemainingHoles

  useEffect(() => {
    if (hasAllPlayableHoles && populateRemainingHoles) {
      setPopulateRemainingHoles(false)
    }
  }, [hasAllPlayableHoles, populateRemainingHoles])

  useEffect(() => {
    if (guidedGreenAutoMapActive || guidedGreenAutoMapCompletedOpen || !shouldOfferGuidedGreenAutoMap) {
      setGuidedGreenAutoMapPromptOpen(false)
    }
  }, [guidedGreenAutoMapActive, guidedGreenAutoMapCompletedOpen, shouldOfferGuidedGreenAutoMap])

  useEffect(() => {
    if (selectedGpsVersion === null) {
      return
    }
    if (lastPromptedGpsVersionRef.current === selectedGpsVersion) {
      return
    }
    if (guidedGreenAutoMapActive || guidedGreenAutoMapCompletedOpen) {
      lastPromptedGpsVersionRef.current = selectedGpsVersion
      return
    }
    if (!shouldOfferGuidedGreenAutoMap) {
      return
    }

    lastPromptedGpsVersionRef.current = selectedGpsVersion
    setGuidedGreenAutoMapPromptOpen(true)
  }, [selectedGpsVersion, guidedGreenAutoMapActive, guidedGreenAutoMapCompletedOpen, shouldOfferGuidedGreenAutoMap])

  const handleStartGuidedGreenAutoMap = useCallback(() => {
    setGuidedGreenAutoMapPromptOpen(false)

    if (!greenMapping) {
      toggleGreenMapping()
    }

    onStartGuidedGreenAutoMap()
  }, [greenMapping, onStartGuidedGreenAutoMap, toggleGreenMapping])

  useEffect(() => {
    if (makeOfficialDisabled && makeOfficial) {
      setMakeOfficial(false)
    }
  }, [makeOfficial, makeOfficialDisabled])

  const handleSuccessClose = () => {
    setSaveDialogOpen(false)
    dispatch(clearGpsVersionStatus())
  }

  const handleClearMap = () => {
    onRecordBeforeChange()
    resetMarkers([], [])
  }

  const handleResetAll = () => {
    onRecordBeforeChange()
    resetMarkers()
  }

  const holesWithMarkers = useMemo(() => {
    return new Set(markers.map((marker) => marker.hole))
  }, [markers])

  const activeHolePolygon = useMemo(() => {
    return polygons.find((polygon) => (polygon.hole ?? activeHole) === activeHole)
  }, [activeHole, polygons])

  const formatGreenAutoMapValue = useCallback((config: GreenAutoMapSliderConfig, value: number) => {
    if (config.formatValue) {
      return config.formatValue(value)
    }

    if (Number.isInteger(config.step)) {
      return value.toFixed(0)
    }

    return value.toFixed(1)
  }, [])

  const greenAutoMapDefaultsActive = useMemo(() => {
    return (Object.keys(DEFAULT_GREEN_AUTO_MAP_PARAMS) as GreenAutoMapParamKey[])
      .filter((key) => !greenAutoMapResetExcludedKeys.includes(key))
      .every((key) => greenAutoMapParams[key] === DEFAULT_GREEN_AUTO_MAP_PARAMS[key])
  }, [greenAutoMapParams, greenAutoMapResetExcludedKeys])

  useEffect(() => {
    greenAutoMapParamsRef.current = greenAutoMapParams
    setGreenAutoMapDraftParams({
      ...greenAutoMapParams,
    })
  }, [greenAutoMapParams])

  useEffect(() => {
    greenAutoMapDraftParamsRef.current = greenAutoMapDraftParams
  }, [greenAutoMapDraftParams])

  const updateGreenAutoMapDraftParam = useCallback(
    (key: GreenAutoMapParamKey, value: GreenAutoMapParams[GreenAutoMapParamKey]) => {
      setGreenAutoMapDraftParams((prev) => {
        if (prev[key] === value) {
          return prev
        }

        return {
          ...prev,
          [key]: value,
        }
      })
    },
    [],
  )

  const commitGreenAutoMapParam = useCallback(
    (key: GreenAutoMapParamKey, value: GreenAutoMapParams[GreenAutoMapParamKey], shouldRemap = false) => {
      if (greenAutoMapParamsRef.current[key] === value) {
        return
      }

      onGreenAutoMapParamChange(key, value)

      if (shouldRemap) {
        onAutoMapGreen({
          ...greenAutoMapDraftParamsRef.current,
          [key]: value,
        })
      }
    },
    [onAutoMapGreen, onGreenAutoMapParamChange],
  )

  const commitActiveGreenAutoMapSlider = useCallback(() => {
    const activeKey = activeGreenAutoMapSliderKeyRef.current

    if (!activeKey) {
      return
    }

    activeGreenAutoMapSliderKeyRef.current = null
    const draftValue = greenAutoMapDraftParamsRef.current[activeKey] as number
    const committedValue = greenAutoMapParamsRef.current[activeKey] as number

    if (draftValue === committedValue) {
      return
    }

    commitGreenAutoMapParam(activeKey, draftValue, true)
  }, [commitGreenAutoMapParam])

  useEffect(() => {
    const handlePointerRelease = () => {
      commitActiveGreenAutoMapSlider()
    }

    window.addEventListener('pointerup', handlePointerRelease)
    window.addEventListener('pointercancel', handlePointerRelease)

    return () => {
      window.removeEventListener('pointerup', handlePointerRelease)
      window.removeEventListener('pointercancel', handlePointerRelease)
    }
  }, [commitActiveGreenAutoMapSlider])

  const maybeRenderToolbar = () => {
    const allowedPhases = [
      GPS_EDITOR_TUTORIAL.Start,
      GPS_EDITOR_TUTORIAL.Toolbar,
      GPS_EDITOR_TUTORIAL.Halfway,
      GPS_EDITOR_TUTORIAL.Finish,
      GPS_EDITOR_TUTORIAL.Done,
    ]

    if (!hasExistingGpsPoints && !allowedPhases.includes(gpsEditorTutorialPhase as GPS_EDITOR_TUTORIAL)) {
      return <ToolbarPlaceholder />
    }

    return (
      <ToolbarStack>
        <CourseGpsEditorGreenAutoMapControls
          activeGreenAutoMapSliderKeyRef={activeGreenAutoMapSliderKeyRef}
          activeHolePolygonPointsCount={activeHolePolygon?.points.length || 0}
          commitActiveGreenAutoMapSlider={commitActiveGreenAutoMapSlider}
          commitGreenAutoMapParam={commitGreenAutoMapParam}
          formatGreenAutoMapValue={formatGreenAutoMapValue}
          greenAutoMapAdvancedOpen={greenAutoMapAdvancedOpen}
          greenAutoMapBusy={greenAutoMapBusy}
          greenAutoMapControlsVisible={greenAutoMapControlsVisible}
          greenAutoMapDefaultsActive={greenAutoMapDefaultsActive}
          greenAutoMapDraftParams={greenAutoMapDraftParams}
          greenAutoMapPreviewReady={greenAutoMapPreviewReady}
          guidedGreenAutoMapActive={guidedGreenAutoMapActive}
          guidedGreenAutoMapProgressLabel={guidedGreenAutoMapProgressLabel}
          greenMapping={greenMapping}
          greenPolygonFinished={greenPolygonFinished}
          onAcceptGreenAutoMapPreview={onAcceptGreenAutoMapPreview}
          onConfirmManualGuidedGreen={onConfirmManualGuidedGreen}
          onAutoMapGreen={onAutoMapGreen}
          onClearGreenPolygon={onClearGreenPolygon}
          onCloseGreenAutoMapControls={onCloseGreenAutoMapControls}
          onFinishGreenPolygon={onFinishGreenPolygon}
          onResetGreenAutoMapParams={onResetGreenAutoMapParams}
          setGreenAutoMapAdvancedOpen={setGreenAutoMapAdvancedOpen}
          updateGreenAutoMapDraftParam={updateGreenAutoMapDraftParam}
          showAdvancedPanel={hasAdminAccess}
        />

        <ToolbarWrapper>
          <CourseGpsEditorToolbar
            undoDisabled={!canUndo}
            redoDisabled={!canRedo}
            holeTool={holeToolEnabled}
            greenMapping={greenMapping}
            onSelectEditTool={selectEditTool}
            onUndo={onUndo}
            onRedo={onRedo}
            onToggleHoleTool={toggleHoleTool}
            onToggleGreenMapping={toggleGreenMapping}
          />
        </ToolbarWrapper>
      </ToolbarStack>
    )
  }

  if (!hasAdminAccess && !isGpsEditor) {
    return null
  }

  const handleSaveButtonClick = (anchorEl?: HTMLElement | null) => {
    if (hasAdminAccess) {
      setSavePopoverAnchorEl(anchorEl || actionButtonsRef.current)
      setSavePopoverOpen(true)
      return
    }
    setSaveDialogOpen(true)
  }

  const handleSaveAndFinish = () => {
    handleSubmit(makeOfficial, populateRemainingHoles)
  }

  const handleAcknowledgeSavePopoverSuccess = () => {
    setSavePopoverOpen(false)
    setSavePopoverAnchorEl(null)
    dispatch(clearGpsVersionStatus())
  }

  const maybeRenderTutorial = () => {
    // Hide the tutorial for now
    // eslint-disable-next-line no-constant-condition
    if (1) {
      return null
    }

    const courseGpsPoints = course.totalGpsPoints ? course.totalGpsPoints : 0
    const isFirstGpsVersion = courseGpsPoints < 3

    if (isFirstGpsVersion && !locationSelected) {
      return null
    }

    return (
      <CourseGpsEditorTutorial
        enabled
        showHalfwayDialog={nonFairwayMarkersCount === maxGpsPoints / 2}
        showFinishedDialog={isFirstGpsVersion && nonFairwayMarkersCount === maxGpsPoints}
      />
    )
  }

  const courseIsLoading = status === 'loading'
  if (courseIsLoading) {
    return <OverlayLoader visible />
  }

  const atLeastOneHoleAdded = markers.length >= 2

  return (
    <>
      <CourseGpsEditorHeader
        autoLoad={autoLoad}
        clubName={course.club?.name}
        courseName={course.name}
        onClearMap={handleClearMap}
        onLocationSelected={() => setLocationSelected(true)}
        onOpenSidebar={showSidebar ? () => setSidebarOpen(true) : undefined}
        onResetAll={handleResetAll}
        placeChanged={placeChanged}
        searchPlaceholder={intl.formatMessage({ id: 'strings.searchLocation' })}
      />

      {maybeRenderTutorial()}

      {showSidebar && (
        <CourseGpsEditorSidebar
          open={sidebarOpen}
          onClose={() => setSidebarOpen(false)}
          onToggleSidebar={() => setSidebarOpen((prev) => !prev)}
          isAdminView={hasAdminAccess}
          onFinishEditing={handleSaveButtonClick}
          isFinishEditingDisabled={finishEditingDisabled}
          syncSelectedVersion={syncSelectedVersion}
          versionIsDirty={versionIsDirty}
          resetZoom={resetZoom}
          handleSubmit={handleSubmit}
          gpsData={markers}
        />
      )}

      <CourseGpsEditorGreenAutoMapDialog
        open={guidedGreenAutoMapPromptOpen}
        title="Auto-map greens"
        description="18 holes are marked down. Allow our automatic green mapper to map all 18 greens?"
        imageSrc={GreenAutomapperScreenshot}
        imageAlt="Green automapper preview"
        primaryActionLabel="Start mapping"
        onPrimaryAction={handleStartGuidedGreenAutoMap}
        onClose={() => setGuidedGreenAutoMapPromptOpen(false)}
        secondaryActionLabel="Not now"
        onSecondaryAction={() => setGuidedGreenAutoMapPromptOpen(false)}
      />

      <CourseGpsEditorGreenAutoMapSuccessDialog
        open={guidedGreenAutoMapCompletedOpen}
        onPrimaryAction={onCloseGuidedGreenAutoMapCompleted}
        onClose={onCloseGuidedGreenAutoMapCompleted}
      />

      {saveDialogOpen && !saveSucceeded && (
        <ConfirmGpsSaveDialog handleSubmit={handleSubmit} close={() => setSaveDialogOpen(false)} />
      )}
      {saveSucceeded && !savePopoverOpen && <CourseGpsSaveSuccessDialog close={handleSuccessClose} />}

      <CourseGpsHoleNavigator
        activeHole={activeHole}
        atLeastOneHoleAdded={atLeastOneHoleAdded}
        goToHole={goToHole}
        goToNextHole={goToNextHole}
        goToPreviousHole={goToPreviousHole}
        holesWithMarkers={holesWithMarkers}
      />

      {maybeRenderToolbar()}

      <ActionButtons ref={actionButtonsRef}>
        {showSidebar && (
          <IconButton
            color="primary"
            style={{ margin: '-8px', color: '#fff' }}
            onClick={() => setSidebarOpen((prev) => !prev)}
          >
            <DrawerToggleLeftIcon />
          </IconButton>
        )}
        {hasAdminAccess ? (
          <SaveButton
            status={'idle'}
            handleSubmit={() => handleSaveButtonClick(actionButtonsRef.current)}
            text={
              <SaveButtonText>
                {intl.formatMessage({ id: 'strings.finishEditing' })}
                {hasAdminAccess && <KeyboardArrowDown fontSize="small" htmlColor="#fff" />}
              </SaveButtonText>
            }
            disabled={finishEditingDisabled}
          />
        ) : (
          <CustomTooltip title={intl.formatMessage({ id: 'strings.sendGpsDataTooltip' })}>
            <SaveButton
              status={'idle'}
              handleSubmit={() => handleSaveButtonClick(actionButtonsRef.current)}
              text={
                <SaveButtonText>
                  {intl.formatMessage({ id: 'strings.finishEditing' })}
                  {hasAdminAccess && <KeyboardArrowDown fontSize="small" htmlColor="#fff" />}
                </SaveButtonText>
              }
              disabled={finishEditingDisabled}
            />
          </CustomTooltip>
        )}
      </ActionButtons>

      {hasAdminAccess && (
        <CourseGpsEditorSavePopover
          open={savePopoverOpen}
          anchorEl={savePopoverAnchorEl}
          canPopulateIncompleteCourse={canPopulateIncompleteCourse}
          disabled={saveAndFinishDisabled}
          makeOfficialDisabled={makeOfficialDisabled}
          makeOfficialDisabledReason={makeOfficialDisabledReason}
          makeOfficial={makeOfficial}
          populateRemainingHoles={populateRemainingHoles}
          isDraftSave={isDraftSave}
          status={saveStatus}
          onClose={() => {
            if (saveStatus === 'loading') {
              return
            }
            setSavePopoverOpen(false)
            setSavePopoverAnchorEl(null)
            if (saveStatus === 'succeeded') {
              dispatch(clearGpsVersionStatus())
            }
          }}
          onMakeOfficialChange={setMakeOfficial}
          onPopulateRemainingHolesChange={setPopulateRemainingHoles}
          onSaveAndFinish={handleSaveAndFinish}
          onAcknowledgeSuccess={handleAcknowledgeSavePopoverSuccess}
        />
      )}

      <MapControlIcons>
        {mapTiltAvailable && (
          <RotateControls $active={mapTiltEnabled}>
            {mapTiltEnabled && (
              <>
                <span onClick={() => triggerMapRotate('clockwise')}>
                  <ClockwiseIcon />
                </span>
                <HorizontalIconDivider />
                <span onClick={() => triggerMapRotate('counterclockwise')}>
                  <CounterClockwiseIcon />
                </span>
                <HorizontalIconDivider />
              </>
            )}
            <TiltButton onClick={() => triggerMapTilt()} $active={mapTiltEnabled}>
              <MapTilesIcon />
            </TiltButton>
          </RotateControls>
        )}
        <ZoomControls>
          <span onClick={() => triggerZoom('+')}>+</span>
          <HorizontalIconDivider />
          <span onClick={() => triggerZoom('-')}>-</span>
        </ZoomControls>
      </MapControlIcons>
    </>
  )
}
